import { isNil } from 'lodash';

import numOrDefault from 'lib/helpers/numOrDefault';
import { ia, iaRa } from 'lib/helpers/utility';

import { amountToCents } from 'components/practice/BeyondBilling/Claims/CMS1500/lib/amountToCents';
import {
  modifyPackages,
  modifyProducts
} from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/internal/configs';
import { renderType } from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/renderType';
import {
  denied_codes,
  patient_responsibility
} from 'components/practice/BeyondBilling/lib/insuranceAdjustmentGroups';

export const primaryCalculation = (procedure) => {
  let ins_payment = 0;
  let ins_adjustment = 0;
  let pt_adjustment = 0;
  let ins_balance = 0;
  const other_adj = procedure?.other_adj || 0;
  const ins_types = ['tertiary', 'secondary', 'primary'];
  const qty = numOrDefault(procedure?.units ?? procedure?.sales_count) || 1;

  const currentCharge = ['self_pay', 'cash'].includes(procedure.type)
    ? (procedure.pt_balance ?? +procedure?.total_cost_cents ?? 0)
    : procedure.charge || 0;

  const charge = qty * currentCharge;

  ins_types?.forEach((insType) => {
    if (ia(procedure?.[insType])) {
      const adjustments = procedure[insType];
      const lastAdjustment = adjustments[adjustments.length - 1];

      ins_payment += Math.round(numOrDefault(lastAdjustment?.paid));
    }
  });

  const activeInsType = ins_types.find((insType) => ia(procedure?.[insType]));
  const adjustments = procedure?.[activeInsType];
  let insAdj = 0;
  if (adjustments) {
    const insTypeLength = adjustments?.length - 1 || 0;
    const lastAdjustment = adjustments[insTypeLength];

    if (ia(lastAdjustment?.pt_adj)) {
      lastAdjustment?.pt_adj?.forEach((adj) => {
        const { group, code, amount } = adj;

        if (lastAdjustment?.patient_responsibility === false) {
          if (!(group === 'PR' && +code === 45)) {
            pt_adjustment += amountToCents(amount);
          }
        } else {
          pt_adjustment += amountToCents(amount);
        }
      });
    }

    if (ia(lastAdjustment?.ins_adj)) {
      lastAdjustment?.ins_adj?.forEach((adj) => {
        const { group, code, amount, type = '' } = adj;

        const hasPRCode = patient_responsibility[group]?.includes(+code) || type == 'pt_rep';
        const hasDeniedCode = denied_codes[group]?.includes(code);

        if (type == 'ins_balance') {
          ins_balance += amountToCents(amount);
        } else if (type == 'adj') {
          insAdj += amountToCents(amount);
        } else if (lastAdjustment?.patient_responsibility === false) {
          if (!(group === 'PR' && +code === 45)) {
            if (group === 'PR' || hasPRCode || hasDeniedCode) {
              pt_adjustment += amountToCents(amount);
            }
          }
        } else {
          if (group === 'PR' || hasPRCode || hasDeniedCode) {
            pt_adjustment += amountToCents(amount);
          }
        }
      });
    }

    const hasNonZeroInsAdj =
      ia(lastAdjustment?.ins_adj) &&
      lastAdjustment.ins_adj.some((adj) => amountToCents(adj.amount) !== 0);

    if (!hasNonZeroInsAdj && !lastAdjustment?.paid) {
      ins_balance = charge - other_adj;
    }
  }

  insAdj +=
    numOrDefault(charge) -
    numOrDefault(ins_payment) -
    numOrDefault(pt_adjustment) -
    numOrDefault(ins_balance) -
    numOrDefault(other_adj);

  if (['product', 'package', 'self_pay'].includes(procedure.type || 0) || procedure?.denied) {
    insAdj = 0;
  }

  ins_adjustment = insAdj;
  const currentInsBalance =
    ins_balance > 0
      ? numOrDefault(ins_balance)
      : numOrDefault(charge) -
        numOrDefault(ins_payment) -
        numOrDefault(ins_adjustment) -
        numOrDefault(pt_adjustment) -
        numOrDefault(other_adj);

  return {
    charge,
    ins_adjustment,
    pt_adjustment,
    ins_payment,
    other_adj,
    ins_balance: currentInsBalance
  };
};

const secondCalculation = (procedure) => {
  const { ins_payment = 0, pt_balance = 0 } = procedure;
  const final_ins_payment = ins_payment - pt_balance;

  return Math.max(final_ins_payment, 0);
};

export function calculateProceduresTotal(procedures) {
  let final_charge = 0;
  let final_ins_payment = 0;
  let final_ins_adjustment = 0;
  let final_pt_adjustment = 0;
  let final_other_adjustment = 0;
  let final_qty = 0;
  let final_ins_balance = 0;

  procedures?.forEach?.((procedure) => {
    const qty =
      numOrDefault(procedure?.units ?? procedure?.quantity ?? procedure?.sales_count) || 1;

    final_qty += qty;

    const qtyToMultiply =
      !procedure?.type || ['product', 'package'].includes(procedure?.type?.toLowerCase()) ? 1 : qty;

    if (Object?.keys?.(procedure).includes('primary')) {
      const {
        charge = 0,
        ins_adjustment = 0,
        pt_adjustment = 0,
        ins_payment = 0,
        other_adj = 0,
        ins_balance = 0
      } = primaryCalculation(procedure);

      final_charge += numOrDefault(charge);
      final_ins_adjustment += numOrDefault(ins_adjustment);
      final_ins_payment += numOrDefault(ins_payment);
      final_pt_adjustment += numOrDefault(pt_adjustment);
      final_other_adjustment += numOrDefault(other_adj);
      final_ins_balance += numOrDefault(ins_balance);
    } else if (
      procedure?.ins_payment > 0 &&
      procedure?.ins_adjustment > 0 &&
      procedure?.charge === procedure?.ins_payment + procedure?.ins_adjustment
    ) {
      const {
        charge = 0,
        pt_adjustment = 0,
        ins_adjustment = 0,
        pt_balance,
        other_adj = 0
      } = procedure;

      const ins_payment = secondCalculation(procedure);

      final_charge += (charge || pt_balance || 0) * qtyToMultiply;
      final_ins_adjustment += numOrDefault(ins_adjustment * qtyToMultiply);
      final_ins_payment += numOrDefault(ins_payment * qtyToMultiply);
      final_pt_adjustment += numOrDefault(pt_adjustment);
      final_other_adjustment += numOrDefault(other_adj);
    } else {
      if (procedure?.status !== 'cancelled') {
        let {
          charge = 0,
          total_amount_cents = 0,
          pt_balance,
          total_cost_cents = 0,
          charge_type,
          ins_payment,
          ins_adjustment,
          other_adj = 0,
          type,
          denied = false
        } = procedure || {};

        let insAdj = 0;

        if (type !== 'primary') {
          insAdj = Math.max(charge - pt_balance, 0);
        }

        if (['product', 'package', 'self_pay', 'cash', 'unapplied'].includes(type) || denied) {
          insAdj = 0;
          charge = 0;
        }

        if (
          isNil(pt_balance) &&
          (charge_type == 'copay' || charge_type == 'primary') &&
          isNil(ins_payment) &&
          isNil(ins_adjustment)
        ) {
          insAdj = charge;
        }

        final_charge +=
          ((+charge || pt_balance) ?? (+total_cost_cents || +total_amount_cents)) * qtyToMultiply;
        final_ins_adjustment += numOrDefault(insAdj * qtyToMultiply);
        final_pt_adjustment += numOrDefault(pt_balance);
        final_other_adjustment += numOrDefault(other_adj);
      }
    }
  });

  return {
    charge: final_charge,
    ins_adjustment: final_ins_adjustment,
    pt_adjustment: final_pt_adjustment,
    ins_payment: final_ins_payment,
    other_adj: final_other_adjustment,
    qty: final_qty,
    ins_balance: final_ins_balance
  };
}

export const getProceduresTotal = (invoices) => {
  const services = [];
  const procedures = invoices
    .map((invoice) => iaRa(invoice.procedures).filter((procedure) => procedure.show) || [])
    .flat();

  if (procedures?.length) {
    services.push(...procedures);
  }
  const products = invoices
    .map((invoice) =>
      modifyProducts({
        products: iaRa(invoice?.products).filter((product) => product.show) || [],
        isTotal: true
      })
    )
    .flat();
  if (products?.length) {
    services.push(...products);
  }
  const packages = invoices
    .map((invoice) =>
      modifyPackages({
        packages: iaRa(invoice?.packages).filter((pkg) => pkg.show) || [],
        isTotal: true
      })
    )
    .flat();
  if (packages?.length) {
    services.push(...packages);
  }

  const { amount_paid, prevDiscount, tax, write_off, ptBalance, totalAmount } = invoices.reduce(
    (acc, invoice) => {
      acc.amount_paid += invoice?.amount_paid || 0;
      acc.prevDiscount += invoice?.discount || 0;
      acc.tax += invoice?.tax || 0;
      acc.write_off += invoice?.write_off || 0;
      acc.ptBalance += invoice?.invoice_balance || invoice?.balance || 0;
      acc.totalAmount += invoice?.total_amount || 0;

      return acc;
    },
    { amount_paid: 0, prevDiscount: 0, tax: 0, write_off: 0, ptBalance: 0, totalAmount: 0 }
  );

  const updatedProceduresType = services.map((service) => ({
    ...service,
    type: service?.primary ? renderType(service) : service?.type
  }));

  const calculatedProcedures = calculateProceduresTotal(updatedProceduresType);

  return {
    ...calculatedProcedures,
    amount_paid,
    prevDiscount,
    tax,
    write_off,
    ptBalance,
    totalAmount
  };
};
